import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Image, Video, Eye } from "lucide-react";
import { format } from "date-fns";
import { ImageUploader, MultiImageUploader } from "./ImageUploader";
import { AIGalleryTools } from "./AIContentTools";
import { GalleryPreview } from "./ContentPreview";

interface Gallery {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  featured_image_url: string | null;
  gallery_type: string;
  event_date: string | null;
  venue: string | null;
  seo_title: string | null;
  seo_description: string | null;
  is_published: boolean;
  is_featured: boolean;
  created_at: string;
}

interface GalleryImage {
  id?: string;
  url: string;
  alt_text?: string;
}

interface Tag {
  id: string;
  name: string;
  slug: string;
}

export const GalleryManager = () => {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingGallery, setEditingGallery] = useState<Gallery | null>(null);
  const { toast } = useToast();

  // Form state
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [featuredImage, setFeaturedImage] = useState("");
  const [galleryType, setGalleryType] = useState<string>("wedding");
  const [eventDate, setEventDate] = useState("");
  const [venue, setVenue] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [videoUrls, setVideoUrls] = useState<string[]>([]);

  useEffect(() => {
    fetchGalleries();
    fetchTags();
  }, []);

  const fetchGalleries = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("galleries")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast({ title: "Error loading galleries", description: error.message, variant: "destructive" });
    } else {
      setGalleries(data || []);
    }
    setIsLoading(false);
  };

  const fetchTags = async () => {
    const { data } = await supabase.from("tags").select("*").order("name");
    setTags(data || []);
  };

  const openDialog = async (gallery?: Gallery) => {
    if (gallery) {
      setEditingGallery(gallery);
      setTitle(gallery.title);
      setSlug(gallery.slug);
      setDescription(gallery.description || "");
      setFeaturedImage(gallery.featured_image_url || "");
      setGalleryType(gallery.gallery_type);
      setEventDate(gallery.event_date || "");
      setVenue(gallery.venue || "");
      setSeoTitle(gallery.seo_title || "");
      setSeoDescription(gallery.seo_description || "");
      setIsPublished(gallery.is_published);
      setIsFeatured(gallery.is_featured);

      // Fetch gallery images
      const { data: images } = await supabase
        .from("gallery_images")
        .select("*")
        .eq("gallery_id", gallery.id)
        .order("sort_order");
      setGalleryImages(images?.map((i) => ({ id: i.id, url: i.image_url, alt_text: i.alt_text })) || []);

      // Fetch gallery videos
      const { data: videos } = await supabase
        .from("gallery_videos")
        .select("*")
        .eq("gallery_id", gallery.id)
        .order("sort_order");
      setVideoUrls(videos?.map((v) => v.video_url) || []);

      // Fetch tags
      const { data: galleryTags } = await supabase
        .from("gallery_tags")
        .select("tag_id")
        .eq("gallery_id", gallery.id);
      setSelectedTags(galleryTags?.map((t) => t.tag_id) || []);
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingGallery(null);
    setTitle("");
    setSlug("");
    setDescription("");
    setFeaturedImage("");
    setGalleryType("wedding");
    setEventDate("");
    setVenue("");
    setSeoTitle("");
    setSeoDescription("");
    setIsPublished(false);
    setIsFeatured(false);
    setSelectedTags([]);
    setGalleryImages([]);
    setVideoUrls([]);
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const cleanJsonResponse = (result: string): string => {
    // Remove markdown code blocks if present
    let cleaned = result.replace(/```json\s*/gi, '').replace(/```\s*/g, '');
    // Try to extract JSON object or array from the string
    const jsonMatch = cleaned.match(/[\[{][\s\S]*[\]}]/);
    return jsonMatch ? jsonMatch[0].trim() : cleaned.trim();
  };

  const handleAIResult = (type: string, result: string) => {
    switch (type) {
      case "gallery-description":
        setDescription(result);
        break;
      case "seo-meta":
        try {
          const cleaned = cleanJsonResponse(result);
          const seoData = JSON.parse(cleaned);
          if (seoData.seoTitle) setSeoTitle(seoData.seoTitle);
          if (seoData.seoDescription) setSeoDescription(seoData.seoDescription);
        } catch (error) {
          console.error("Failed to parse SEO data:", result, error);
          toast({ title: "Could not parse SEO data", variant: "destructive" });
        }
        break;
      case "suggest-tags":
        try {
          const cleaned = cleanJsonResponse(result);
          const suggestedTags = JSON.parse(cleaned);
          const matchingTagIds = tags
            .filter((t) => suggestedTags.includes(t.name))
            .map((t) => t.id);
          setSelectedTags(matchingTagIds);
        } catch (error) {
          console.error("Failed to parse tags:", result, error);
          toast({ title: "Could not parse tags", variant: "destructive" });
        }
        break;
      case "generate-slug":
        setSlug(result.trim().replace(/['"]/g, ""));
        break;
    }
  };

  const saveGallery = async () => {
    if (!title || !slug) {
      toast({ title: "Title and slug are required", variant: "destructive" });
      return;
    }

    try {
      // Get current user for created_by tracking
      const { data: { user } } = await supabase.auth.getUser();

      const galleryData = {
        title,
        slug,
        description: description || null,
        featured_image_url: featuredImage || null,
        gallery_type: galleryType,
        event_date: eventDate || null,
        venue: venue || null,
        seo_title: seoTitle || null,
        seo_description: seoDescription || null,
        is_published: isPublished,
        is_featured: isFeatured,
      };

      let galleryId = editingGallery?.id;

      if (editingGallery) {
        const { error } = await supabase
          .from("galleries")
          .update(galleryData)
          .eq("id", editingGallery.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("galleries")
          .insert({ ...galleryData, created_by: user?.id })
          .select()
          .single();
        if (error) throw error;
        galleryId = data.id;
      }

      // Update images
      if (galleryId) {
        await supabase.from("gallery_images").delete().eq("gallery_id", galleryId);
        if (galleryImages.length > 0) {
          await supabase.from("gallery_images").insert(
            galleryImages.map((img, index) => ({
              gallery_id: galleryId,
              image_url: img.url,
              alt_text: img.alt_text || null,
              sort_order: index,
            }))
          );
        }

        // Update videos
        await supabase.from("gallery_videos").delete().eq("gallery_id", galleryId);
        if (videoUrls.filter((v) => v).length > 0) {
          await supabase.from("gallery_videos").insert(
            videoUrls
              .filter((v) => v)
              .map((url, index) => ({
                gallery_id: galleryId,
                video_url: url,
                video_type: url.includes("youtube") ? "youtube" : url.includes("vimeo") ? "vimeo" : "upload",
                sort_order: index,
              }))
          );
        }

        // Update tags
        await supabase.from("gallery_tags").delete().eq("gallery_id", galleryId);
        if (selectedTags.length > 0) {
          await supabase.from("gallery_tags").insert(
            selectedTags.map((tagId) => ({ gallery_id: galleryId, tag_id: tagId }))
          );
        }
      }

      toast({ title: editingGallery ? "Gallery updated!" : "Gallery created!" });
      setIsDialogOpen(false);
      fetchGalleries();
    } catch (error: any) {
      toast({ title: "Error saving gallery", description: error.message, variant: "destructive" });
    }
  };

  const deleteGallery = async (id: string) => {
    if (!confirm("Delete this gallery?")) return;

    const { error } = await supabase.from("galleries").delete().eq("id", id);
    if (error) {
      toast({ title: "Error deleting gallery", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Gallery deleted" });
      fetchGalleries();
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  const addVideoUrl = () => setVideoUrls([...videoUrls, ""]);
  const updateVideoUrl = (index: number, value: string) => {
    const updated = [...videoUrls];
    updated[index] = value;
    setVideoUrls(updated);
  };
  const removeVideoUrl = (index: number) => {
    setVideoUrls(videoUrls.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-foreground">Galleries</h3>
        <Button onClick={() => openDialog()} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          New Gallery
        </Button>
      </div>

      <div className="rounded-xl bg-card border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {galleries.map((gallery) => (
              <TableRow key={gallery.id}>
                <TableCell className="font-medium">{gallery.title}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">
                    {gallery.gallery_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={gallery.is_published ? "default" : "secondary"}>
                    {gallery.is_published ? "Published" : "Draft"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {gallery.is_featured ? (
                    <Badge className="bg-amber-100 text-amber-800">Featured</Badge>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {gallery.event_date
                    ? format(new Date(gallery.event_date), "MMM d, yyyy")
                    : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(gallery)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteGallery(gallery.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {galleries.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No galleries yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingGallery ? "Edit Gallery" : "New Gallery"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            <AIGalleryTools
              title={title}
              description={description}
              galleryType={galleryType}
              venue={venue}
              onResult={handleAIResult}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (!editingGallery) setSlug(generateSlug(e.target.value));
                  }}
                  placeholder="Gallery title"
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="gallery-url-slug"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="galleryType">Gallery Type</Label>
                <Select value={galleryType} onValueChange={setGalleryType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wedding">Wedding</SelectItem>
                    <SelectItem value="engagement">Engagement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="eventDate">Event Date</Label>
                <Input
                  id="eventDate"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="venue">Venue</Label>
                <Input
                  id="venue"
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  placeholder="Wedding venue"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Gallery description..."
                rows={4}
              />
            </div>

            <div>
              <Label>Featured Image</Label>
              <ImageUploader
                bucket="gallery-images"
                folder="featured"
                value={featuredImage}
                onChange={setFeaturedImage}
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Image className="h-4 w-4" />
                <Label>Gallery Images</Label>
              </div>
              <MultiImageUploader
                bucket="gallery-images"
                folder="gallery"
                images={galleryImages}
                onChange={setGalleryImages}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  <Label>Video Links</Label>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addVideoUrl}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Video
                </Button>
              </div>
              <div className="space-y-2">
                {videoUrls.map((url, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={url}
                      onChange={(e) => updateVideoUrl(index, e.target.value)}
                      placeholder="YouTube or Vimeo URL"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeVideoUrl(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <Switch
                  id="published"
                  checked={isPublished}
                  onCheckedChange={setIsPublished}
                />
                <Label htmlFor="published">Published</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="featured"
                  checked={isFeatured}
                  onCheckedChange={setIsFeatured}
                />
                <Label htmlFor="featured" className="flex items-center gap-1">
                  Featured on Homepage
                </Label>
              </div>
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant={selectedTags.includes(tag.id) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleTag(tag.id)}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">SEO Settings</h4>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="seoTitle">SEO Title</Label>
                  <Input
                    id="seoTitle"
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    placeholder="SEO optimized title (60 chars max)"
                    maxLength={60}
                  />
                </div>
                <div>
                  <Label htmlFor="seoDescription">SEO Description</Label>
                  <Textarea
                    id="seoDescription"
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    placeholder="Meta description (160 chars max)"
                    maxLength={160}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="secondary" onClick={() => setIsPreviewOpen(true)}>
                <Eye className="mr-2 h-4 w-4" />
                Preview
              </Button>
              <Button onClick={saveGallery}>
                {editingGallery ? "Update Gallery" : "Create Gallery"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <GalleryPreview
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        gallery={{
          title,
          description,
          featuredImage,
          galleryType,
          eventDate,
          venue,
          images: galleryImages,
          tags: tags.filter((t) => selectedTags.includes(t.id)).map((t) => t.name),
        }}
      />
    </div>
  );
};
